(() => {
	const reduceMotionQuery = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	);
	const animationContexts = new Map();
	const splitTextInstances = new Map();
	let smoothScroll;
	let isInitialized = false;

	const initializeSmoothScroll = () => {
		if (!window.Lenis) return;

		smoothScroll = new window.Lenis({
			anchors: true,
			overscroll: false
		});

		smoothScroll.on('scroll', ScrollTrigger.update);
		gsap.ticker.add(time => smoothScroll.raf(time * 1000));
		gsap.ticker.lagSmoothing(0);
	};

	const scrollToCurrentAnchor = () => {
		const targetId = decodeURIComponent(window.location.hash.slice(1));
		const target = document.getElementById(targetId);

		if (!target) return;

		if (smoothScroll) {
			smoothScroll.scrollTo(target, { immediate: true });
			return;
		}

		target.scrollIntoView();
	};

	const createScrollTrigger = (trigger, start = 'top 82%') => ({
		trigger,
		start,
		once: true
	});

	const animateSectionHeader = section => {
		const header = section.querySelector('.tr-section__header');

		if (!header) return;

		const elements = header.querySelectorAll(
			'.tr-title, .tr-section__description'
		);

		if (!elements.length) return;

		gsap.from(elements, {
			y: 42,
			autoAlpha: 0,
			duration: 0.8,
			stagger: 0.14,
			ease: 'power3.out',
			scrollTrigger: createScrollTrigger(header)
		});
	};

	const animateFounderStory = section => {
		const description = section.querySelector('.tr-founder-story__description');

		if (description && window.SplitText) {
			const splitText = new window.SplitText(description, {
				type: 'lines',
				linesClass: 'tr-scroll-animation-line'
			});

			splitTextInstances.set(section.id, splitText);

			gsap.from(splitText.lines, {
				y: 18,
				autoAlpha: 0,
				duration: 1.05,
				stagger: 0.18,
				ease: 'power2.out',
				scrollTrigger: createScrollTrigger(description, 'top 76%')
			});
		} else if (description) {
			gsap.from(description, {
				y: 18,
				autoAlpha: 0,
				duration: 0.9,
				ease: 'power2.out',
				scrollTrigger: createScrollTrigger(description, 'top 76%')
			});
		}
	};

	const animateHero = section => {
		const content = section.querySelector('.tr-hero__content');
		const background = section.querySelector('.tr-hero__background-image');
		const elements = section.querySelectorAll(
			'.tr-hero__eyebrow, .tr-hero__title, .tr-hero__description, .tr-hero__buttons'
		);

		if (background) {
			gsap.fromTo(
				background,
				{ scale: 1.12 },
				{ scale: 1, duration: 1.7, ease: 'power2.out' }
			);
		}

		if (elements.length) {
			gsap.from(elements, {
				y: 34,
				autoAlpha: 0,
				duration: 0.8,
				stagger: 0.12,
				ease: 'power3.out',
				delay: 0.16
			});
		}

		if (content) {
			gsap.to(content, {
				yPercent: -8,
				ease: 'none',
				scrollTrigger: {
					trigger: section,
					start: 'top top',
					end: 'bottom top',
					scrub: true
				}
			});
		}
	};

	const animateMetrics = section => {
		const cards = section.querySelectorAll('.tr-metrics__card');

		if (!cards.length) return;

		gsap.from(cards, {
			y: 54,
			autoAlpha: 0,
			duration: 0.75,
			stagger: 0.12,
			ease: 'back.out(1.3)',
			scrollTrigger: createScrollTrigger(section)
		});
	};

	const animateCards = (section, selector) => {
		const cards = section.querySelectorAll(selector);

		if (!cards.length) return;

		gsap.from(cards, {
			y: 38,
			autoAlpha: 0,
			duration: 0.65,
			stagger: 0.1,
			ease: 'power3.out',
			scrollTrigger: createScrollTrigger(section)
		});
	};

	const animateTrackRecord = section => {
		const cards = section.querySelectorAll('.tr-track-record__card');

		cards.forEach((card, index) => {
			const image = card.querySelector('.tr-track-record__image');
			const title = card.querySelector('.tr-track-record__card-title');
			const direction = index % 2 === 0 ? -1 : 1;
			const timeline = gsap.timeline({
				scrollTrigger: createScrollTrigger(card, 'top 80%')
			});

			if (image)
				timeline.from(image, {
					xPercent: direction * 18,
					scale: 1.08,
					autoAlpha: 0,
					duration: 0.9,
					ease: 'power3.out'
				});
			if (title)
				timeline.from(
					title,
					{ y: 26, autoAlpha: 0, duration: 0.55, ease: 'power3.out' },
					image ? '-=0.38' : 0
				);
		});
	};

	const animateCta = section => {
		const inner = section.querySelector('.tr-cta__inner');
		const background = section.querySelector('.tr-cta__background-image');
		const elements = section.querySelectorAll('.tr-cta__title, .tr-cta__body');

		if (inner && elements.length) {
			gsap.from(elements, {
				y: 30,
				autoAlpha: 0,
				duration: 0.7,
				stagger: 0.13,
				ease: 'power3.out',
				scrollTrigger: createScrollTrigger(inner, 'top 78%')
			});
		}

		if (inner && background) {
			gsap.fromTo(
				background,
				{ scale: 1.12 },
				{
					scale: 1,
					ease: 'none',
					scrollTrigger: {
						trigger: inner,
						start: 'top bottom',
						end: 'bottom top',
						scrub: true
					}
				}
			);
		}
	};

	const createAnimations = section => {
		const context = gsap.context(() => {
			if (section.querySelector('.tr-hero')) animateHero(section);
			if (section.querySelector('.tr-founder-story'))
				animateFounderStory(section);
			if (section.querySelector('.tr-metrics')) animateMetrics(section);
			if (section.querySelector('.tr-how-it-works'))
				animateCards(section, '.tr-how-it-works__card');
			if (section.querySelector('.tr-track-record'))
				animateTrackRecord(section);
			if (section.querySelector('.tr-founder-logos'))
				animateCards(section, '.tr-founder-logos__logo');
			if (section.querySelector('.tr-faq'))
				animateCards(section, '.tr-faq__accordion');
			if (section.querySelector('.tr-cta')) animateCta(section);
			if (!section.querySelector('.tr-services')) animateSectionHeader(section);
		}, section);

		animationContexts.set(section.id, context);
	};

	const destroyAnimations = section => {
		const context = animationContexts.get(section.id);

		if (!context) return;

		context.revert();
		animationContexts.delete(section.id);

		const splitText = splitTextInstances.get(section.id);

		if (splitText) {
			splitText.revert();
			splitTextInstances.delete(section.id);
		}
	};

	const refreshScrollBounds = () => {
		smoothScroll?.resize();
		ScrollTrigger.refresh();
	};

	const initializeAnimations = (scope = document) => {
		scope.querySelectorAll('[id^="shopify-section-"]').forEach(section => {
			destroyAnimations(section);
			createAnimations(section);
		});

		refreshScrollBounds();
	};

	const onSectionLoad = event => {
		const section = document.getElementById(
			`shopify-section-${event.detail.sectionId}`
		);

		if (!section) return;

		destroyAnimations(section);
		createAnimations(section);
		refreshScrollBounds();
	};

	const initialize = () => {
		if (isInitialized) return;

		isInitialized = true;

		if (reduceMotionQuery.matches || !window.gsap || !window.ScrollTrigger)
			return;

		gsap.registerPlugin(ScrollTrigger);
		if (window.SplitText) gsap.registerPlugin(window.SplitText);
		initializeSmoothScroll();
		window.requestAnimationFrame(scrollToCurrentAnchor);
		window.addEventListener(
			'load',
			() => {
				refreshScrollBounds();
				scrollToCurrentAnchor();
			},
			{ once: true }
		);
		initializeAnimations();
		document.addEventListener('shopify:section:load', onSectionLoad);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initialize, { once: true });
	} else {
		initialize();
	}
})();
